import { formatBytes, formatDuration } from '../lib/emitter.ts';
import { describeTrack, muteState, type CapturedSource } from '../lib/sources.ts';
import type { StudioEngine } from '../lib/studio.ts';
import { Icon, Meter, MuteBadge, useNow, VideoEl } from './common.tsx';

/**
 * The "what am I sharing / what am I recording" panel. Every source has two
 * independent switches, so it's always obvious what others see and what gets saved.
 */
export function SourcesPanel({ engine }: { engine: StudioEngine }) {
  const sm = engine.sources;
  useNow(1000);
  const free = engine.storageFree;
  const perSec = engine.recordingBytesPerSec;
  const pressure = engine.cpuPressure;
  return (
    <>
      <p className="panel-note">
        <strong>Live</strong> is what the others see and hear. <strong>Record</strong> saves a full-quality copy on this computer
        and uploads it for the edit.
      </p>
      {engine.loadLevel > 0 && (
        <div className="alert warn small">
          <Icon name="shield" size={14} />
          <span>
            <strong>Protecting your recording.</strong> This computer is busy, so things that matter less are turned down
            first: uploads wait, then live video, then recorded frame rate. Sound is never reduced.
          </span>
        </div>
      )}
      {sm.active.map((s) => (
        <SourceCard key={s.id} engine={engine} source={s} />
      ))}
      {!sm.active.length && (
        <div className="empty">
          <Icon name="layers" size={22} />
          No sources yet. Add a camera, microphone or screen from the bar below.
        </div>
      )}
      <div className="stats">
        {free != null && (
          <div className="stat" title="Space the browser lets this site use">
            <span className="k">Free space</span>
            <span className="v">
              {formatBytes(free)}
              {engine.recording && perSec > 0 ? <span className="faint small"> · ~{formatDuration((free / perSec) * 1000)}</span> : null}
            </span>
          </div>
        )}
        <div className="stat" title="How precisely this computer's clock matches the studio server">
          <span className="k">Clock sync</span>
          <span className="v">±{engine.signaling ? Math.round(engine.signaling.clock.uncertaintyMs) : '?'} ms</span>
        </div>
        {pressure !== 'unknown' && (
          <div className={`stat ${pressure === 'critical' || pressure === 'serious' ? 'warn' : ''}`}>
            <span className="k">Processor load</span>
            <span className="v" style={{ textTransform: 'capitalize' }}>{pressure}</span>
          </div>
        )}
      </div>
    </>
  );
}

function SourceCard({ engine, source: s }: { engine: StudioEngine; source: CapturedSource }) {
  const sm = engine.sources;
  const rec = engine.recorders.get(s.id)?.recorder;
  const isVideo = s.kind === 'camera' || s.kind === 'screen';
  const devices = s.kind === 'camera' ? sm.cameras : s.kind === 'microphone' ? sm.microphones : [];
  const mute = muteState(s);
  return (
    <div className="source">
      <div className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
        <div className="thumb">{isVideo ? <VideoEl track={s.track} /> : <Icon name={s.kind === 'microphone' ? 'mic' : 'speaker'} size={20} />}</div>
        <div className="col grow" style={{ gap: 1 }}>
          <input className="name" value={s.label} onChange={(e) => sm.rename(s.id, e.target.value)} aria-label="Source name" title="Rename" />
          <span className="sub">{describeTrack(s)}</span>
        </div>
        <button className="ghost sm icon" title={s.kind === 'screen' && sm.soundOf(s) ? 'Stop sharing (and its sound)' : 'Remove source'} aria-label="Remove source" onClick={() => sm.remove(s.id)}>
          <Icon name="close" size={14} />
        </button>
      </div>
      {(s.kind === 'microphone' || s.kind === 'system-audio') && <Meter track={s.track} />}
      <div className="opt-row">
        <div className="switches" role="group" aria-label="Use">
          <button className={`switch ${s.use.live ? 'on-live' : ''}`} aria-pressed={s.use.live} onClick={() => sm.setUse(s.id, { live: !s.use.live })} title="Send to the call as you">
            <Icon name={s.use.live ? 'eye' : 'eyeOff'} size={14} /> Live
          </button>
          <button className={`switch ${s.use.record ? 'on-rec' : ''}`} aria-pressed={s.use.record} onClick={() => sm.setUse(s.id, { record: !s.use.record })} title="Save a full-quality local recording">
            <Icon name="rec" size={11} /> Record
          </button>
        </div>
        {devices.length > 1 && (
          <select className="sm" value={s.deviceId} onChange={(e) => void sm.switchDevice(s.id, e.target.value)} style={{ maxWidth: 150 }} aria-label="Device">
            {devices.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || d.deviceId.slice(0, 6)}
              </option>
            ))}
          </select>
        )}
      </div>
      {rec && (
        <div className="rec-line">
          <span className={`pill ${rec.state === 'error' ? 'bad' : 'rec'}`}>
            <span className={`dot ${rec.state === 'recording' ? 'pulse' : ''}`} /> {rec.state === 'recording' ? 'REC' : rec.state}
          </span>
          {rec.state === 'recording' && (
            <span>
              {formatDuration(rec.lastMediaTime * 1000)} · {formatBytes(rec.durableBytes)}
            </span>
          )}
          {rec.width && (
            <span className={rec.frameDivisor > 1 ? '' : 'faint'} style={rec.frameDivisor > 1 ? { color: 'var(--warn)' } : undefined}>
              {rec.width}×{rec.height} · {rec.fps ? Math.round(rec.fps / rec.frameDivisor) : '?'} fps{rec.frameDivisor > 1 ? ' (reduced)' : ''}
            </span>
          )}
        </div>
      )}
      <div className="opt-row">
        <span className="label">Mute</span>
        <div className="toggle" role="radiogroup" aria-label={`Mute ${s.label}`}>
          <button className={mute === 'none' ? 'active' : ''} role="radio" aria-checked={mute === 'none'} onClick={() => engine.setMute(s.id, 'none')} title="Sent (if Live) and recorded (if Record)">
            On
          </button>
          <button className={mute === 'send' ? 'active send-muted' : ''} role="radio" aria-checked={mute === 'send'} onClick={() => engine.setMute(s.id, 'send')} title="Not sent to the call, still recorded">
            Mute sending
          </button>
          <button className={mute === 'hard' ? 'active hard-muted' : ''} role="radio" aria-checked={mute === 'hard'} onClick={() => engine.setMute(s.id, 'hard')} title="Not sent and not recorded – silence/black is written so the file stays in sync">
            Hard mute
          </button>
        </div>
      </div>
      <MuteBadge kind={s.kind} mute={mute} />
    </div>
  );
}
